import { toApiError } from '@/lib/http';

/**
 * Finding what the published khutbahs already say about a topic.
 *
 * The response is assembled from rows, not written by a model, so everything
 * here is quotable: a passage, the khutbah it came from, and the provenance
 * needed to cite it. Nothing in this module composes text.
 */

export interface TopicMatch {
  readonly passageId: string;
  readonly ordinal: number;
  readonly passageType: string;
  readonly sectionType: string;
  /** The Arabic as published, tashkeel intact. */
  readonly sourceText: string;
  /** The reading language, or null when this passage has no published translation. */
  readonly translationText: string | null;
  readonly translationStatus: string | null;
  /**
   * Who produced the translation: `publisher`, `human` or `ai`. The badge
   * needs it — calling a translator's own unreviewed work an AI translation
   * misstates its provenance as badly as the reverse would.
   */
  readonly translationOrigin: 'publisher' | 'human' | 'ai' | null;
  readonly translationReviewed: boolean;
  readonly uncertain: boolean;
}

export interface TopicResult {
  readonly khutbahId: string;
  readonly friday: string;
  readonly institutionKey: string;
  readonly institutionName: string;
  readonly institutionNameAr: string | null;
  readonly verifiedPublisher: boolean;
  readonly sourceKind: 'publisher' | 'user';
  readonly titleOriginal: string | null;
  /** How many passages matched in total — more than are returned. */
  readonly matchCount: number;
  readonly matches: readonly TopicMatch[];
}

function str(source: Record<string, unknown>, key: string): string | null {
  const value = source[key];
  return typeof value === 'string' && value !== '' ? value : null;
}

/**
 * Narrows one match, or drops it.
 *
 * The server omits null properties (`default-property-inclusion: non_null`),
 * so an absent `translationText` is the normal shape for a passage with no
 * published translation in this language — not a malformed response.
 */
/** Anything the server did not name explicitly is not assumed to be a person's. */
function parseOrigin(value: string | null): 'publisher' | 'human' | 'ai' | null {
  return value === 'publisher' || value === 'human' || value === 'ai' ? value : null;
}

function parseMatch(value: unknown): TopicMatch[] {
  if (typeof value !== 'object' || value === null) return [];
  const match = value as Record<string, unknown>;
  const passageId = str(match, 'passageId');
  const sourceText = str(match, 'sourceText');
  if (passageId === null || sourceText === null) return [];
  return [
    {
      passageId,
      ordinal: typeof match.ordinal === 'number' ? match.ordinal : 0,
      passageType: str(match, 'passageType') ?? 'prose',
      sectionType: str(match, 'sectionType') ?? '',
      sourceText,
      translationText: str(match, 'translationText'),
      translationStatus: str(match, 'translationStatus'),
      translationOrigin: parseOrigin(str(match, 'translationOrigin')),
      // Defaults to unreviewed. Claiming a person checked a translation when
      // the server did not say so is the one direction this must never get
      // wrong, and a citation carries that claim onward.
      translationReviewed: match.translationReviewed === true,
      uncertain: match.uncertain === true,
    },
  ];
}

function parseResult(value: unknown): TopicResult[] {
  if (typeof value !== 'object' || value === null) return [];
  const result = value as Record<string, unknown>;
  const khutbahId = str(result, 'khutbahId');
  const friday = str(result, 'friday');
  if (khutbahId === null || friday === null) return [];
  return [
    {
      khutbahId,
      friday,
      institutionKey: str(result, 'institutionKey') ?? '',
      institutionName: str(result, 'institutionName') ?? '',
      institutionNameAr: str(result, 'institutionNameAr'),
      verifiedPublisher: result.verifiedPublisher === true,
      sourceKind: result.sourceKind === 'user' ? 'user' : 'publisher',
      titleOriginal: str(result, 'titleOriginal'),
      matchCount: typeof result.matchCount === 'number' ? result.matchCount : 0,
      matches: Array.isArray(result.matches)
        ? result.matches.flatMap(parseMatch)
        : [],
    },
  ];
}

/** The shortest term the server will act on. Below this it returns nothing. */
export const MIN_TOPIC_LENGTH = 2;

export async function fetchTopic(
  topic: string,
  language: string,
  signal: AbortSignal,
): Promise<TopicResult[]> {
  const search = new URLSearchParams({ topic, language });
  const response = await fetch(`/api/public/prepare?${search}`, {
    signal,
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) {
    throw await toApiError(response);
  }
  const body: unknown = await response.json();
  return Array.isArray(body) ? body.flatMap(parseResult) : [];
}
